import {
  CategoryChildren,
  DownloadFileData,
  DownloadFileItem,
  ProtectedFileModel,
  DownloadFileModel,
  DownloadsCategoryModel,
  DownloadsSearchResult,
  FileFavoritesModel,
} from '../models/downloads.model'
import { FetchErrorCodes, StatusCodes } from '@e3dc-react/shell/Libs'
import { handleFetchError } from 'src/Libs/api/utils'
import {
  downloadsCategoryDtoToModelTransformer,
  downloadsItemDtoToModelTransformer,
  downloadsSearchResultDtoToModelTransformer,
} from '../transformer'
import { CategoryChildrenDto, DownloadsSearchResultDto, DownloadFileItemDto, FileFavoritesDto } from '../dto'
import { CreateCategoryFormValues } from '../components'
import { categoryChildrenDtoToModelTransformer } from '../transformer/category-children-dto-to-model-transformer'
import { transformCategoryPayload } from '../utils'
import { fileFavoritesDtoToModelTransformer } from '../transformer/file-favorites-dto-to-model-transformer'
import { ApiResult } from 'src/Libs/api'
import { AuthFetchError } from '@e3dc-react/shell'
import { AuthFetchWithResponse } from 'src/Types/api'

/**
 * Fetch a download category by its ID
 * Includes parents and children
 */
export const getDownloadCategory = async (
  categoryId: number,
  authFetch: AuthFetchWithResponse,
  filter?: string
): Promise<ApiResult<DownloadsCategoryModel>> => {
  try {
    const response = await authFetch<DownloadsCategoryModel>({
      path: `files/category/${categoryId}`,
      method: 'GET',
      endpoint: process.env.REACT_APP_API_ENDPOINT,
      payload: { sort: 'id+', filter },
    })

    if (response.status === StatusCodes.OK) {
      const dataModel = downloadsCategoryDtoToModelTransformer(response.data)

      return new ApiResult<DownloadsCategoryModel, AuthFetchError | Error>(dataModel)
    }
    return new ApiResult<DownloadsCategoryModel, AuthFetchError | Error>(null)
  } catch (error) {
    return handleFetchError<DownloadsCategoryModel>(error)
  }
}

/**
 * Fetch a single download file by its ID
 */
export const getDownloadFile = async (fileId: number, authFetch: AuthFetchWithResponse, filter?: string): Promise<ApiResult<DownloadFileItem>> => {
  try {
    const response = await authFetch<DownloadFileItemDto>({
      path: `files/file/${fileId}`,
      method: 'GET',
      endpoint: process.env.REACT_APP_API_ENDPOINT,
    })

    if (response.status === StatusCodes.OK) {
      const dataModel = downloadsItemDtoToModelTransformer(response.data)
      return new ApiResult<DownloadFileItem, AuthFetchError | Error>(dataModel)
    }
    return new ApiResult<DownloadFileItem, AuthFetchError | Error>(null)
  } catch (error) {
    return handleFetchError<DownloadFileItem>(error)
  }
}

/**
 * Get search results for download files
 */
export const getDownloadSearchResults = async (authFetch: AuthFetchWithResponse, filter?: string): Promise<ApiResult<DownloadsSearchResult[]>> => {
  try {
    const response = await authFetch<DownloadsSearchResultDto[]>({
      path: 'files/file/search',
      method: 'GET',
      endpoint: process.env.REACT_APP_API_ENDPOINT,
      payload: { sort: 'fileName+', filter },
    })

    if (response.status === StatusCodes.OK) {
      const dataModel = response.data.map(item => downloadsSearchResultDtoToModelTransformer(item))

      return new ApiResult<DownloadsSearchResult[], AuthFetchError | Error>(dataModel)
    }
    return new ApiResult<DownloadsSearchResult[], AuthFetchError | Error>(null)
  } catch (error) {
    return handleFetchError<DownloadsSearchResult[]>(error)
  }
}

/**
 * Get download link for a single file
 */
export const getSingleDownloadFile = async (
  fileId: number,
  forceDownload: boolean,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<DownloadFileModel>> => {
  try {
    const response = await authFetch<DownloadFileModel>({
      path: `files/file/${fileId}`,
      method: 'GET',
      endpoint: process.env.REACT_APP_API_ENDPOINT,
      payload: { forceDownload },
    })

    if (response.status === StatusCodes.OK) {
      const dataModel = response.data

      return new ApiResult<DownloadFileModel, AuthFetchError | Error>(dataModel)
    }
    return new ApiResult<DownloadFileModel, AuthFetchError | Error>(null)
  } catch (error) {
    return handleFetchError<DownloadFileModel>(error)
  }
}

/**
 * Create a new category
 */
export const createCategory = async (categoryData: CreateCategoryFormValues, authFetch: AuthFetchWithResponse): Promise<ApiResult<void>> => {
  const transformedPayload = transformCategoryPayload(categoryData)

  const response = await authFetch<void>({
    path: `files/category`,
    method: 'POST',
    payload: transformedPayload,
  })

  if (response.status === StatusCodes.CREATED) {
    return new ApiResult<void>(null, false)
  }

  return new ApiResult<void>(null, true)
}

/**
 * Update an existing category
 */
export const updateCategory = async (
  categoryId: number,
  categoryData: Omit<CreateCategoryFormValues, 'parentId' | 'path'>,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<void>> => {
  const transformedPayload = transformCategoryPayload(categoryData)

  const response = await authFetch<void>({
    path: `files/category/${categoryId}`,
    method: 'PUT',
    payload: transformedPayload,
  })

  if (response.status === StatusCodes.OK) {
    return new ApiResult<void>(null, false)
  }

  return new ApiResult<void>(null, true)
}

/**
 * Delete a category
 */
export const deleteCategory = async (categoryId: number, recursive: boolean, authFetch: AuthFetchWithResponse): Promise<ApiResult<void>> => {
  const response = await authFetch<void>({
    path: `files/category/${categoryId}?recursive=${recursive}`,
    method: 'DELETE',
  })

  if (response.status === StatusCodes.OK) {
    return new ApiResult<void>(null, false)
  }

  return new ApiResult<void>(null, true)
}

/**
 * get a Count of all childrens in a Category
 */
export const getCategoryAndFileCount = async (categoryId: number, authFetch: AuthFetchWithResponse): Promise<ApiResult<CategoryChildren>> => {
  const response = await authFetch<CategoryChildrenDto>({
    path: `files/category/${categoryId}/childrens-count`,
    method: 'GET',
  })

  if (response.status === StatusCodes.OK) {
    const model = categoryChildrenDtoToModelTransformer(response.data)
    return new ApiResult<CategoryChildren>(model, false)
  }

  return new ApiResult<CategoryChildren>(null, true)
}

/**
 * Move a category or Item to a new parent category
 */
export const moveDownloadItem = async (downloadItemId: number, newParentId: number, authFetch: AuthFetchWithResponse): Promise<ApiResult<void>> => {
  const response = await authFetch<void>({
    path: `files/category/${newParentId}/move/${downloadItemId}`,
    method: 'PUT',
    payload: {},
  })

  if (response.status === StatusCodes.NO_CONTENT) {
    return new ApiResult<void>(null, false)
  }

  return new ApiResult<void>(null, true)
}

/**
 * upload a file
 */
export const uploadFile = async (payload: FormData, authFetch: AuthFetchWithResponse): Promise<ApiResult<void>> => {
  const response = await authFetch<void>({
    path: `files/file`,
    method: 'POST',
    payload,
  })

  if (response.status === StatusCodes.NO_CONTENT) {
    return new ApiResult<void>(null, false)
  }

  return new ApiResult<void>(null, true)
}

/**
 * replace a file
 */
export const replaceFile = async (fileId: number, payload: FormData, authFetch: AuthFetchWithResponse): Promise<ApiResult<void>> => {
  const response = await authFetch<void>({
    path: `files/file/${fileId}/replace`,
    method: 'PUT',
    payload,
  })

  if (response.status === StatusCodes.OK) {
    return new ApiResult<void>(null, false)
  }

  return new ApiResult<void>(null, true)
}

/**
 * update a file
 */
export const updateFile = async (
  fileId: number,
  payload: Omit<DownloadFileData, 'path'>,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<void>> => {
  const response = await authFetch<void>({
    path: `files/file/${fileId}`,
    method: 'PUT',
    payload,
  })

  if (response.status === StatusCodes.NO_CONTENT) {
    return new ApiResult<void>(null, false)
  }

  return new ApiResult<void>(null, true)
}

/**
 * Delete a file
 */
export const deleteDownloadFile = async (fileId: number, authFetch: AuthFetchWithResponse): Promise<ApiResult<void>> => {
  const response = await authFetch<void>({
    path: `files/file/${fileId}`,
    method: 'DELETE',
  })

  if (response.status === StatusCodes.OK) {
    return new ApiResult<void>(null, false)
  }

  return new ApiResult<void>(null, true)
}

/**
 * test if a url name is valid, meaning not already in use
 */
export const validateUrlName = async (urlName: string, authFetch: AuthFetchWithResponse): Promise<ApiResult<boolean>> => {
  try {
    const response = await authFetch<void>({
      path: `files/file/url-name-check/${urlName}`,
      method: 'GET',
    })

    if (response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult<boolean>(true, false)
    }
    return new ApiResult<boolean>(false, true)
  } catch (error) {
    const err = error as AuthFetchError

    if (err.response?.data.errno === FetchErrorCodes.UrlNameAlreadyExists) {
      return new ApiResult<boolean>(false, false)
    }

    console.error('Error validating URL name:', error)
    return new ApiResult<boolean>(false, true)
  }
}

/**
 * will return a downloadItem id and its parent if a protected file with the given fileName exists and user has the right to access it
 */
export const getProtectedDownlodFile = async (fileName: string, authFetch: AuthFetchWithResponse): Promise<ApiResult<ProtectedFileModel>> => {
  const response = await authFetch<ProtectedFileModel>({
    path: `files/file/protected/${fileName}`,
    method: 'GET',
  })

  if (response.status === StatusCodes.OK) {
    return new ApiResult<ProtectedFileModel>(response.data, false)
  }

  return new ApiResult<ProtectedFileModel>(null, true)
}

/**
 * remove favorite from file
 */
export const removeFileFavorite = async (fileId: number, authFetch: AuthFetchWithResponse): Promise<ApiResult<void>> => {
  const response = await authFetch<void>({
    path: `files/file/${fileId}/remove-favorite`,
    method: 'DELETE',
  })

  if (response.status === StatusCodes.NO_CONTENT) {
    return new ApiResult<void>(null, false)
  }
  return new ApiResult<void>(null, true)
}

/**
 * set a file as favorite
 */
export const setFileFavorite = async (downloadItemId: number, authFetch: AuthFetchWithResponse): Promise<ApiResult<void>> => {
  const response = await authFetch<void>({
    path: `files/file/favorite`,
    method: 'POST',
    payload: { downloadItemId },
  })
  if (response.status === StatusCodes.CREATED || response.status === StatusCodes.OK) {
    return new ApiResult<void>(null, false)
  }
  return new ApiResult<void>(null, true)
}
/*
 * get favorite files for current user
 */
export const getFileFavorites = async (authFetch: AuthFetchWithResponse): Promise<ApiResult<FileFavoritesModel[]>> => {
  const response = await authFetch<FileFavoritesDto>({
    path: `files/file/favorite`,
    method: 'GET',
  })

  if (response.status === StatusCodes.OK) {
    const model = fileFavoritesDtoToModelTransformer(response.data)
    return new ApiResult<FileFavoritesModel[]>(model, false)
  }
  return new ApiResult<FileFavoritesModel[]>(null, true)
}
